export interface MatchRange {
    start: number;
    end: number;
}

/**
 * Letters that carry a diacritic Unicode will not decompose for us. NFD turns
 * "ó" into "o" plus a combining mark, but "ł" and friends are single code
 * points with no decomposition, so they need spelling out by hand.
 */
const NON_DECOMPOSING_FOLDS: Record<string, string> = {
    'ł': 'l',
    'đ': 'd',
    'ø': 'o',
    'æ': 'ae',
    'œ': 'oe',
    'ß': 'ss',
    'ħ': 'h',
    'ŧ': 't',
    'ı': 'i',
};

const COMBINING_MARKS = /\p{M}/gu;

const foldCharacter = (character: string): string => {
    const lowered = character.toLowerCase();
    const stripped = lowered.normalize('NFD').replace(COMBINING_MARKS, '');
    return NON_DECOMPOSING_FOLDS[stripped] ?? stripped;
};

interface FoldedText {
    folded: string;
    /** For each character of `folded`, the index it came from in the original. */
    sourceIndexes: number[];
}

/**
 * Folds text for comparison — lowercased and stripped of diacritics — while
 * remembering where each folded character came from, so a match found in the
 * folded text can still be highlighted in the original.
 *
 * A single source character can fold to several characters ("ß" to "ss") or to
 * none (a lone combining mark), which is exactly why the mapping cannot be
 * assumed to be one to one.
 */
export const foldForSearch = (text: string): FoldedText => {
    let folded = '';
    const sourceIndexes: number[] = [];

    for (let index = 0; index < text.length; index += 1) {
        const replacement = foldCharacter(text[index]);
        folded += replacement;
        for (let offset = 0; offset < replacement.length; offset += 1) {
            sourceIndexes.push(index);
        }
    }

    return {folded, sourceIndexes};
};

/**
 * Every place `term` occurs in `text`, ignoring case and diacritics, as ranges
 * into the original string. "czesc" finds "Cześć"; "CZEŚĆ" finds it too.
 */
export const findMatchRanges = (text: string, term: string): MatchRange[] => {
    if (!text || !term) return [];

    const {folded, sourceIndexes} = foldForSearch(text);
    const {folded: foldedTerm} = foldForSearch(term);
    if (!foldedTerm) return [];

    const ranges: MatchRange[] = [];
    let searchFrom = 0;

    while (searchFrom <= folded.length - foldedTerm.length) {
        const foundAt = folded.indexOf(foldedTerm, searchFrom);
        if (foundAt === -1) break;

        const start = sourceIndexes[foundAt];
        const lastFoldedIndex = foundAt + foldedTerm.length - 1;
        const end = sourceIndexes[lastFoldedIndex] + 1;

        // Folding can collapse characters, so two folded matches may land on the
        // same source range; keep one entry per distinct span.
        const previous = ranges[ranges.length - 1];
        if (!previous || previous.start !== start || previous.end !== end) {
            ranges.push({start, end});
        }

        searchFrom = foundAt + foldedTerm.length;
    }

    return ranges;
};

export const matchesSearchTerm = (text: string, term: string): boolean =>
    findMatchRanges(text, term).length > 0;
